import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  useMediaQuery
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import SendIcon from "@material-ui/icons/Send";
import AssistantOutlinedIcon from "@material-ui/icons/AssistantOutlined";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useOsCatalogs from "../../hooks/useOsCatalogs";

const useStyles = makeStyles(theme => ({
  summary: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default
  },
  summaryLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem"
  },
  description: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  attachmentInput: {
    display: "none"
  },
  attachmentList: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  footerHint: {
    flex: 1,
    color: theme.palette.text.secondary,
    paddingLeft: theme.spacing(1)
  }
}));

const fileSize = bytes => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const clipboardImageName = (file, index) => {
  const extensionByType = {
    "image/bmp": "bmp",
    "image/gif": "gif",
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/svg+xml": "svg",
    "image/webp": "webp"
  };
  const extension = extensionByType[file.type] || "png";
  const now = new Date();
  const date = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0")
  ].join("");
  const time = [
    String(now.getHours()).padStart(2, "0"),
    String(now.getMinutes()).padStart(2, "0"),
    String(now.getSeconds()).padStart(2, "0")
  ].join("");

  return `imagem-colada-${date}-${time}-${index + 1}.${extension}`;
};

const OsInteractionDialog = ({
  open,
  onClose,
  onSaved,
  order,
  objectOptions,
  canFinalize
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [description, setDescription] = useState("");
  const [selectedObjects, setSelectedObjects] = useState([]);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [statusId, setStatusId] = useState(null);
  const [priorityId, setPriorityId] = useState(null);
  const { data: sharedCatalogs } = useOsCatalogs();

  const statusOptions = useMemo(
    () =>
      (sharedCatalogs?.status || []).filter(
        option => String(option.grupo_id) === String(order?.grupo_id)
      ),
    [sharedCatalogs, order]
  );

  useEffect(() => {
    if (open) {
      setDescription("");
      setSelectedObjects([]);
      setFiles([]);
      setStatusId(order?.status_id ?? null);
      setPriorityId(order?.prioridade_id ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const appendFiles = useCallback(incoming => {
    if (!incoming.length) return 0;

    const unique = [...files, ...incoming]
      .filter(
        (file, index, all) =>
          all.findIndex(
            item =>
              item.name === file.name &&
              item.size === file.size &&
              item.lastModified === file.lastModified
          ) === index
      )
      .slice(0, 10);

    const oversized = incoming.find(file => file.size > 20 * 1024 * 1024);
    if (oversized) {
      toast.error(`O arquivo ${oversized.name} ultrapassa 20 MB.`);
      return 0;
    }

    if (unique.length === files.length && incoming.length) {
      toast.info("Essa imagem já está na lista de anexos.");
      return 0;
    }

    if (files.length + incoming.length > 10) {
      toast.warning("É possível anexar no máximo 10 arquivos.");
    }

    setFiles(unique);
    return unique.length - files.length;
  }, [files]);

  const addFiles = event => {
    appendFiles(Array.from(event.target.files || []));
    event.target.value = "";
  };

  const pasteClipboardImage = event => {
    if (submitting || suggesting) return;

    const clipboardFiles = Array.from(event.clipboardData?.items || [])
      .filter(item => item.kind === "file" && item.type.startsWith("image/"))
      .map(item => item.getAsFile())
      .filter(Boolean)
      .map(
        (file, index) =>
          new File([file], clipboardImageName(file, index), {
            type: file.type,
            lastModified: Date.now() + index
          })
      );

    if (!clipboardFiles.length) return;

    event.preventDefault();
    if (files.length >= 10) {
      toast.warning("É possível anexar no máximo 10 arquivos.");
      return;
    }

    const addedCount = appendFiles(clipboardFiles);
    if (!addedCount) return;

    toast.success(
      addedCount === 1
        ? "Imagem colada nos anexos."
        : `${addedCount} imagens coladas nos anexos.`
    );
  };

  const suggestWithAi = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post(
        `/os/ordens-servico/${order.id}/ai-interaction-suggestion`
      );
      const suggestion = data.data || data;
      setDescription(suggestion.descricao || "");
      const suggestedIds = new Set(
        (suggestion.objetos_conclusao_ids || []).map(Number)
      );
      setSelectedObjects(
        objectOptions.filter(option => suggestedIds.has(Number(option.id)))
      );
      toast.success("Rascunho de interação sugerido. Revise antes de salvar.");
    } catch (err) {
      toastError(err);
    } finally {
      setSuggesting(false);
    }
  };

  const submit = async finalize => {
    if (!description.trim()) {
      toast.warning("Informe a descrição da interação.");
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    formData.append("descricao", description.trim());
    formData.append("finalizar", finalize ? "1" : "0");
    if (priorityId && String(priorityId) !== String(order?.prioridade_id)) {
      formData.append("prioridade_id", String(priorityId));
    }
    if (
      !finalize &&
      statusId &&
      String(statusId) !== String(order?.status_id)
    ) {
      formData.append("status_id", String(statusId));
    }
    formData.append(
      "objetos_conclusao",
      JSON.stringify(selectedObjects.map(object => object.id))
    );
    files.forEach(file => formData.append("arquivos", file));

    try {
      await api.post(
        `/os/ordens-servico/${order.id}/interacoes`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(
        finalize
          ? "Interação registrada e OS finalizada."
          : "Interação registrada."
      );
      onSaved(finalize);
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
    >
      <DialogTitle>Nova interação · OS #{order?.id}</DialogTitle>
      <DialogContent dividers onPaste={pasteClipboardImage}>
        <Paper variant="outlined" className={classes.summary}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={5}>
              <Typography className={classes.summaryLabel}>Cliente</Typography>
              <Typography className={classes.description}>
                {order?.cliente?.razao || order?.cliente?.fantasia || "-"}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography className={classes.summaryLabel}>Objeto</Typography>
              <Typography className={classes.description}>
                {order?.objeto?.nome || "-"}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography className={classes.summaryLabel}>Status</Typography>
              <Typography className={classes.description}>
                {order?.status?.nome || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Typography className={classes.summaryLabel}>Nível</Typography>
              <Chip size="small" variant="outlined" label="Padrão" />
            </Grid>
            <Grid item xs={12}>
              <Typography className={classes.summaryLabel}>
                Descrição curta
              </Typography>
              <Typography className={classes.description}>
                {order?.descricao_curta || "-"}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {canFinalize && order?.grupo_id && (
          <Box my={2}>
            <Autocomplete
              options={statusOptions}
              value={
                statusOptions.find(
                  option => String(option.id) === String(statusId)
                ) || null
              }
              onChange={(_, value) => setStatusId(value ? value.id : null)}
              getOptionLabel={option => option.nome || ""}
              getOptionSelected={(option, value) => option.id === value.id}
              disabled={submitting || suggesting}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Status da OS"
                  variant="outlined"
                />
              )}
            />
            <Typography variant="caption" color="textSecondary">
              Altere o status da OS junto com esta interação.
            </Typography>
          </Box>
        )}

        <Box my={2}>
          <Autocomplete
            options={sharedCatalogs?.prioridades || []}
            value={(sharedCatalogs?.prioridades || []).find(
              option => String(option.id) === String(priorityId)
            ) || null}
            onChange={(_, value) => setPriorityId(value?.id ?? null)}
            getOptionLabel={option => option.nome || ""}
            getOptionSelected={(option, value) => option.id === value.id}
            disableClearable
            disabled={submitting || suggesting}
            renderInput={params => (
              <TextField {...params} label="Prioridade da OS" variant="outlined" />
            )}
          />
        </Box>

        <Box display="flex" justifyContent="flex-end" mb={1}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={suggesting ? <CircularProgress size={16} /> : <AssistantOutlinedIcon />}
            onClick={suggestWithAi}
            disabled={submitting || suggesting}
          >
            Sugerir com IA
          </Button>
        </Box>

        <TextField
          autoFocus
          label="Descrição da interação"
          placeholder="Descreva o atendimento realizado, decisões e próximos passos…"
          value={description}
          onChange={event => setDescription(event.target.value)}
          variant="outlined"
          multiline
          rows={8}
          fullWidth
          required
          disabled={submitting || suggesting}
        />

        <Box my={2}>
          <Autocomplete
            multiple
            options={objectOptions}
            value={selectedObjects}
            onChange={(_, value) => setSelectedObjects(value)}
            getOptionLabel={option => option.nome || ""}
            getOptionSelected={(option, value) => option.id === value.id}
            filterSelectedOptions
            disabled={submitting || suggesting}
            noOptionsText="Nenhum objeto encontrado"
            renderInput={params => (
              <TextField
                {...params}
                label="Objetos de conclusão"
                placeholder={
                  selectedObjects.length ? "" : "Selecione um ou mais objetos"
                }
                variant="outlined"
              />
            )}
          />
          <Typography variant="caption" color="textSecondary">
            Use quando a interação concluir ou entregar algum objeto específico.
          </Typography>
        </Box>

        <Divider />
        <Box mt={2}>
          <input
            id="os-interaction-files"
            className={classes.attachmentInput}
            type="file"
            multiple
            onChange={addFiles}
            disabled={submitting || suggesting}
          />
          <label htmlFor="os-interaction-files">
            <Button
              component="span"
              variant="outlined"
              startIcon={<AttachFileIcon />}
              disabled={submitting || files.length >= 10}
            >
              Adicionar anexos
            </Button>
          </label>
          <Typography variant="caption" color="textSecondary">
            {" "}Até 10 arquivos, com no máximo 20 MB cada. Cole imagens com Ctrl+V.
          </Typography>
          <div className={classes.attachmentList}>
            {files.map(file => (
              <Chip
                key={`${file.name}-${file.size}-${file.lastModified}`}
                label={`${file.name} · ${fileSize(file.size)}`}
                onDelete={
                  submitting
                    ? undefined
                    : () => setFiles(current => current.filter(item => item !== file))
                }
              />
            ))}
          </div>
        </Box>
      </DialogContent>
      <DialogActions>
        <Typography variant="caption" className={classes.footerHint}>
          {canFinalize
            ? "Ao finalizar, a OS será movida para o status de conclusão do grupo."
            : "Esta OS já está finalizada; a interação será adicionada ao histórico."}
        </Typography>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={submitting ? <CircularProgress size={18} /> : <SendIcon />}
          onClick={() => submit(false)}
          disabled={submitting || !description.trim()}
        >
          Salvar interação
        </Button>
        {canFinalize && (
          <Button
            variant="contained"
            color="primary"
            startIcon={
              submitting ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <CheckCircleOutlineIcon />
              )
            }
            onClick={() => submit(true)}
            disabled={submitting || !description.trim()}
          >
            Salvar e finalizar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OsInteractionDialog;
